import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { uxWorkflowApi } from "./api";
import { toast } from "@shared/ui/use-toast";
import { queryClient } from "@shared/api/queries/queryClient";

export const useGetAllSegments = () => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.workflow.getAllSegments(),
    () => uxWorkflowApi.getAllSegments(utilityId),
    {
      enabled: !!utilityId,
    }
  );
};

export const useGetAllRecipientList = () => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.workflow.getAllRecipientList(),
    () => uxWorkflowApi.getAllRecipientList(utilityId),
    {
      enabled: !!utilityId,
    }
  );
};

export const useCreateWorkflow = () => {
  return useSmartMutation(
    (payload: any) => uxWorkflowApi.createWorkflow(payload),
    {
      onSuccess: () => {
        toast({
          title: "Workflow create",
          description: "Workflow created successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["ux", "uxWorkflow", "workflowlist"],
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to create Workflow`,
          description: `${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};
export const useReRunWorkflow = () => {
  return useSmartMutation(
    (payload: any) => uxWorkflowApi.reRunWorkflow(payload),
    {
      onSuccess: () => {
        toast({
          title: "Workflow Re-run",
          description: "Workflow Re-run successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["ux", "uxWorkflow", "workflowlist"],
        });
        queryClient.refetchQueries({
          queryKey: ["ux", "workflow", "workflowDetails"],
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to Rre-run Workflow`,
          description: `${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};
export const usePauseWorkflow = () => {
  return useSmartMutation(
    (payload: any) => uxWorkflowApi.pauseWorkflow(payload),
    {
      onSuccess: () => {
        toast({
          title: "Workflow Pause",
          description: "Workflow Pause successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["ux", "uxWorkflow", "workflowlist"],
        });
        queryClient.refetchQueries({
          queryKey: ["ux", "workflow", "workflowDetails"],
        });
      },
      onError: (error: Error) => {
        toast({
          title: `Failed to Pause Workflow`,
          description: `${error.message}`,
          variant: "destructive",
        });
      },
    }
  );
};

export const useGetAllCommunicationTemplatesByChannel = (
  search?: any,
  channel?: any,
  showAddCommunication?: any
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.workflow.getAllCommunicationTemplatesByChannel(
      search,
      channel
    ),
    () =>
      uxWorkflowApi.getAllCommunicationTemplatesByChannel(
        utilityId,
        search,
        channel
      ),
    {
      enabled: !!utilityId || !!channel || showAddCommunication,
    }
  );
};
export const useGetOnlyTemplatesByEmail = (search?: any) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.workflow.getAllCommunicationTemplatesByChannel(
      search
    ),
    () =>
      uxWorkflowApi.getAllCommunicationTemplatesByChannel(utilityId, search),
    {
      enabled: !!utilityId,
    }
  );
};
export const useGetOnlyTemplatesByTextMessage = (search?: any) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.workflow.getAllCommunicationTemplatesByChannel(
      search
    ),
    () =>
      uxWorkflowApi.getAllCommunicationTemplatesByChannel(utilityId, search),
    {
      enabled: !!utilityId,
    }
  );
};
export const useGetWorkflowDetails = (id: any) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.workflow.getWorkflowDetails(id),
    () => uxWorkflowApi.getWorkflowDetails(utilityId, id),
    {
      enabled: !!id,
    }
  );
};

export const useUpdateWorkflow = (workflowId: string) => {
  return useSmartMutation(
    (payload: any): Promise<any> => {
      return uxWorkflowApi.updateWorkflow(payload, workflowId);
    },
    {
      onSuccess: () => {
        toast({
          title: "workflow updated",
          description: "workflow updated successfully!",
          variant: "default",
        });
        queryClient.refetchQueries({
          queryKey: ["ux", "workflow", "workflowDetails"],
        });
        queryClient.refetchQueries({
          queryKey: ["ux", "uxWorkflow", "workflowlist"],
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update segment",
          variant: "destructive",
        });
      },
    }
  );
};

export const useGetAllWorkflows = (
  page?: any,
  limit?: any,
  search?: any,
  audienceType?: any,
  actionType?: any,
  status?: any
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.workflow.getAllWorkflow(
      page,
      limit,
      search,
      audienceType,
      actionType,
      status
    ),
    () =>
      uxWorkflowApi.getAllWorkflows(
        utilityId,
        page,
        limit,
        search,
        audienceType,
        actionType,
        status
      ),
    {
      enabled: !!utilityId,
    }
  );
};

export const useGetAllWorkflowsExecutionDetails = (
  workflowId: string,
  page?: any,
  limit?: any,
  search?: any
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.workflow.getAllWorkflowExecutionDetails(
      workflowId,
      page,
      limit,
      search
    ),
    () =>
      uxWorkflowApi.getAllWorkflowsExecutionDetails(
        utilityId,
        workflowId,
        page,
        limit,
        search
      ),
    {
      enabled: !!utilityId,
    }
  );
};

export const useGetWorkflowsExecutionDetails = (executionId: string) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.workflow.getWorkflowsExecutionDetails(
      executionId
    ),
    () => uxWorkflowApi.getWorkflowsExecutionDetails(utilityId, executionId),
    {
      enabled: !!utilityId,
    }
  );
};

export const useGetWorkflowsExecutionChannelSummary = (
  executionId: string,
  channel: string
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.workflow.getWorkflowsExecutionChannelSummary(
      executionId,
      channel
    ),
    () =>
      uxWorkflowApi.getWorkflowsExecutionChannelSummary(
        utilityId,
        executionId,
        channel
      ),
    {
      enabled: !!utilityId,
    }
  );
};

export const useGetWorkflowsExecutionAllChannelRecipients = (
  executionId: string,
  channel: string,
  page?: any,
  limit?: any,
  search?: any
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.workflow.getAllWorkflowExecutionAllChannelRecipients(
      executionId,
      channel,
      page,
      limit,
      search
    ),
    () =>
      uxWorkflowApi.getWorkflowsExecutionAllChannelRecipients(
        utilityId,
        executionId,
        channel,
        page,
        limit,
        search
      ),
    {
      enabled: !!utilityId,
    }
  );
};

export const useGetAllRunHistory = (
  page?: any,
  limit?: any,
  search?: any,
  executionDateFrom?: string,
  executionDateTo?: string,
  channel?: string,
  audienceType?: string,
  actionType?: string
) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.workflow.getAllRunHistory(
      page,
      limit,
      search,
      executionDateFrom,
      executionDateTo,
      channel,
      audienceType,
      actionType
    ),
    () =>
      uxWorkflowApi.getAllRunHistory(
        utilityId,
        page,
        limit,
        search,
        executionDateFrom,
        executionDateTo,
        channel,
        audienceType,
        actionType
      ),
    {
      enabled: !!utilityId,
    }
  );
};


export const useGetRunHistorySummary = () => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ux.workflow.getRunHistorySummary(),
    () => uxWorkflowApi.getRunHistorySummary(utilityId),
    {
      enabled: !!utilityId,
    }
  );
};
